import { createPinia } from 'pinia';
import SecureLS from 'secure-ls';
import { useAuthStore } from './modules/auth';
import { useUserStore } from './modules/user';

export const ls = new SecureLS({
  encodingType: 'aes',
  isCompression: true,
  encryptionSecret: 'LuIEpuIYx__uiQlMDh1y_mlzDSm9gB4G9ayUtoRS0BhzWmf7IgpwEEbYV3vciLpw',
});

export function securePiniaPlugin({ store, options }) {
  if (options.persist === false) return;

  const storageKey = typeof options.persist === 'object' && options.persist.key
    ? options.persist.key
    : store.$id;

  // 1. Rehydrate state from SecureLS ('vertex_core') on store initialization
  try {
    const storedState = ls.get('vertex_core');
    if (storedState && storedState[storageKey]) {
      store.$patch(storedState[storageKey]);
    }
  } catch (error) {
    console.warn(`[Pinia] Error retrieving stored state for ${store.$id}:`, error);

    // Handle old compression issues or invalid JSON
    if (
      error.message &&
      (error.message.includes('Malformed UTF-8') ||
        error.message.includes('Unexpected token') ||
        error.message.includes('Could not parse JSON'))
    ) {
      console.warn('[Pinia] Detected corrupted storage. Clearing local storage.');
      localStorage.clear();
    }
  }

  // 2. Subscribe to store mutations and save updated state to SecureLS ('vertex_core')
  store.$subscribe(
    (mutation, state) => {
      try {
        const storedState = ls.get('vertex_core') || {};
        storedState[storageKey] = JSON.parse(JSON.stringify(state));
        ls.set('vertex_core', storedState);
      } catch (error) {
        console.error(`[Pinia] Failed to persist store ${store.$id}:`, error);
      }
    },
    { detached: true }
  );
}

export const pinia = createPinia();
pinia.use(securePiniaPlugin);

// Compatibility bridge for legacy Vuex syntax ($store.state, $store.commit, $store.dispatch)
const store = {
  install(app) {
    app.use(pinia);
    app.config.globalProperties.$store = store;
  },
  get state() {
    return {
      get auth() {
        return useAuthStore(pinia);
      },
      get user() {
        return useUserStore(pinia);
      },
    };
  },
  get getters() {
    const resolveStore = (module, getterName) => {
      if (module === 'auth' || !module) {
        const authStore = useAuthStore(pinia);
        if (getterName in authStore) {
          return authStore;
        }
      }
      if (module === 'user' || !module) {
        const userStore = useUserStore(pinia);
        if (getterName in userStore) {
          return userStore;
        }
      }
      return null;
    };

    return new Proxy(
      {},
      {
        get(_, prop) {
          if (typeof prop !== 'string') return undefined;
          const [module, getterName] = prop.includes('/') ? prop.split('/') : ['', prop];
          const targetStore = resolveStore(module, getterName);
          return targetStore ? targetStore[getterName] : undefined;
        },
        has(_, prop) {
          if (typeof prop !== 'string') return false;
          const [module, getterName] = prop.includes('/') ? prop.split('/') : ['', prop];
          return resolveStore(module, getterName) !== null;
        },
      }
    );
  },
  commit(type, payload) {
    const [module, mutation] = type.includes('/') ? type.split('/') : ['', type];
    if (module === 'auth' || !module) {
      const authStore = useAuthStore(pinia);
      if (typeof authStore[mutation] === 'function') {
        return authStore[mutation](payload);
      }
      if (mutation in authStore) {
        authStore[mutation] = payload;
        return;
      }
    }
    if (module === 'user' || !module) {
      const userStore = useUserStore(pinia);
      if (typeof userStore[mutation] === 'function') {
        return userStore[mutation](payload);
      }
      if (mutation in userStore) {
        userStore[mutation] = payload;
        return;
      }
    }
  },
  dispatch(type, payload) {
    const [module, action] = type.includes('/') ? type.split('/') : ['', type];
    if (module === 'auth' || !module) {
      const authStore = useAuthStore(pinia);
      if (typeof authStore[action] === 'function') {
        return authStore[action](payload);
      }
    }
    if (module === 'user' || !module) {
      const userStore = useUserStore(pinia);
      if (typeof userStore[action] === 'function') {
        return userStore[action](payload);
      }
    }
  },
};

export { useAuthStore, useUserStore };
export default store;